// Cross-tenant campaign list (read-only). Drill: /campaigns/:id.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { campaignService } from '../../services/api';
import './Campaigns.css';

// The REAL campaign_status enum (verified against prod): active, paused,
// deleted, archived. 'deleted' is excluded by the route. The earlier list here
// was copied from the V25 migration and offered six values the enum no longer
// has while omitting the two it does — every one of them returned nothing.
const STATUSES = ['', 'active', 'paused', 'archived'];

export default function Campaigns() {
  const [params, setParams] = useSearchParams();
  const tenant = params.get('tenant') || '';
  const status = params.get('status') || '';
  const q = params.get('q') || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(q);

  const setParam = (k, v) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set(k, v); else n.delete(k);
    return n;
  }, { replace: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await campaignService.list({ tenant: tenant || undefined, status: status || undefined, q: q || undefined });
      setRows(res.data); setError(null);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [tenant, status, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => { if (search !== q) setParam('q', search); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="cp-page">
      <div className="page-header">
        <h1>Campaigns</h1>
        <p className="cp-sub">Every tenant, read-only. Delivery truth lives in the drill-down; approvals in Moderation.</p>
      </div>

      <div className="cp-controls">
        <input className="cp-search" placeholder="Search campaign name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={status} onChange={(e) => setParam('status', e.target.value)} aria-label="Status filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s || 'any status'}</option>)}
        </select>
        {tenant && <button type="button" className="btn btn-secondary" onClick={() => setParam('tenant', '')}>Clear tenant filter</button>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <table className="data-table cp-table">
        <thead>
          <tr>
            <th>Campaign</th><th>Tenant</th><th>Status</th><th>Published</th>
            <th>Adsets</th><th>Ads</th><th>Pending review</th><th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan="8" className="cp-empty">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan="8" className="cp-empty">No campaigns match.</td></tr>}
          {!loading && rows.map((c) => (
            <tr key={c.id}>
              <td><Link to={`/campaigns/${c.id}`}>{c.campaign_name}</Link></td>
              <td>{c.tenant_name}</td>
              <td><span className="status-badge">{c.campaign_status}</span></td>
              <td>{c.is_published ? 'yes' : '—'}</td>
              <td>{c.adsets}</td>
              <td>{c.ads}</td>
              <td>{c.pending_reviews > 0
                ? <Link to={`/moderation?queue=ad-reviews`} title="Open the moderation queue">{c.pending_reviews}</Link>
                : '—'}</td>
              <td>{new Date(c.updated_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
